import ExcelJS from 'exceljs';
import { supabase } from '@/lib/supabase';
import { fetchAllActiveAccessories } from '@/services/accessories';
import {
  classifyHeuristicColumn,
  expandLaptopUnits,
  computeLaptopGroupKey,
} from './purchaseSpreadsheetHelpers';

export { classifyHeuristicColumn, expandLaptopUnits, computeLaptopGroupKey };

export interface ExtractedSpreadsheetItem {
  id: string; // temp client ID for review
  item_type: 'laptop' | 'accessory' | 'new_accessory' | 'unknown';
  source_sheet: string;
  source_row: number;
  original_description: string;

  // Laptop fields
  brand?: string;
  model?: string;
  serial_number?: string;
  service_tag?: string;
  barcode?: string;
  cpu?: string;
  ram?: string;
  storage?: string;
  gpu?: string;
  screen_size?: string;
  resolution?: string;
  touchscreen?: boolean | null;
  colour?: string;
  keyboard_layout?: string;
  operating_system?: string;
  condition?: 'new' | 'excellent' | 'good' | 'fair' | 'poor';
  grade?: string;
  battery_health?: number | null;
  charger_included?: boolean;
  charger_notes?: string;
  accessories_included?: string;
  unit_cost: number;
  refurbishment_cost?: number;
  other_cost?: number;
  selling_price?: number; // Optional: Never manufactured from cost
  minimum_price?: number;
  warranty_months?: number;
  description?: string;
  notes?: string;
  is_public?: boolean;

  // Accessory fields
  accessory_id?: string;
  accessory_name?: string;
  accessory_sku?: string;
  accessory_category?: string;
  accessory_brand?: string;
  accessory_barcode?: string;
  quantity?: number;
  reorder_level?: number;
  exact_sku_matched?: boolean;
  suggested_name_match?: string;

  // Validation / Warnings
  warnings: string[];
  duplicate_warning?: string;
  is_valid: boolean;
}

export interface ExtractedSpreadsheetPurchase {
  supplier_id?: string | null;
  supplier_name: string | null;
  supplier_code: string | null;
  invoice_reference: string | null;
  invoice_date: string | null;
  shipping_cost: number;
  other_cost: number;
  discount: number;
  amount_paid: number;
  payment_method: 'cash' | 'bank_transfer' | 'mobile_wallet' | 'other';
  notes: string | null;
  items: ExtractedSpreadsheetItem[];
  extraction_method: 'canonical_deterministic' | 'heuristic_deterministic' | 'ai_gemini';
  warnings: string[];
}

export type PurchaseImportDraft = ExtractedSpreadsheetPurchase;

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_ROWS = 2000;
const MAX_SHEETS = 10;
const MAX_COLUMNS = 100;
const MAX_CELLS = 100000;
export const MAX_STRING_LENGTH = 1000;

export async function parsePurchaseSpreadsheet(file: File): Promise<{
  data: ExtractedSpreadsheetPurchase | null;
  error: string | null;
  notConfigured?: boolean;
}> {
  try {
    // 1. File Type & Size Validation
    if (file.size > MAX_FILE_SIZE) {
      return { data: null, error: `File size (${(file.size / (1024 * 1024)).toFixed(1)} MB) exceeds 10 MB limit.` };
    }

    const nameLower = file.name.toLowerCase();
    if (nameLower.endsWith('.xlsm')) {
      return { data: null, error: 'Macro-enabled workbooks (.xlsm) are not permitted for security reasons. Please save as standard .xlsx or .csv.' };
    }

    if (nameLower.endsWith('.xls')) {
      return { data: null, error: 'Legacy Excel (.xls) is not supported. Please save the spreadsheet as modern Excel (.xlsx) or CSV (.csv).' };
    }

    if (!nameLower.endsWith('.xlsx') && !nameLower.endsWith('.csv')) {
      return { data: null, error: 'Unsupported file format. Please upload an Excel (.xlsx) or CSV (.csv) file.' };
    }

    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();

    try {
      if (nameLower.endsWith('.csv')) {
        const text = new TextDecoder().decode(buffer);
        // Load CSV into workbook
        const ws = workbook.addWorksheet('Sheet1');
        const lines = text.split(/\r?\n/);
        for (let r = 0; r < lines.length; r++) {
          const line = lines[r].trim();
          if (!line) continue;
          // Simple CSV line parser supporting quoted fields
          const rowValues = parseCsvLine(line);
          ws.addRow(rowValues);
        }
      } else {
        await workbook.xlsx.load(buffer);
      }
    } catch (loadErr) {
      return { data: null, error: 'Could not parse workbook. File may be corrupted or invalid.' };
    }

    if (workbook.worksheets.length === 0) {
      return { data: null, error: 'The uploaded workbook contains no sheets.' };
    }

    if (workbook.worksheets.length > MAX_SHEETS) {
      return { data: null, error: `Workbook contains ${workbook.worksheets.length} sheets (maximum allowed is ${MAX_SHEETS}).` };
    }

    // 2. Enforce Strict Workbook Dimension Boundaries (Reject, never truncate)
    let totalCellCount = 0;
    for (const ws of workbook.worksheets) {
      const rowCount = ws.rowCount;
      if (rowCount > MAX_ROWS) {
        return {
          data: null,
          error: `Sheet "${ws.name}" contains ${rowCount} rows, exceeding the maximum limit of ${MAX_ROWS} rows. Please split into smaller batches.`,
        };
      }

      const colCount = ws.columnCount;
      if (colCount > MAX_COLUMNS) {
        return {
          data: null,
          error: `Sheet "${ws.name}" contains ${colCount} columns, exceeding the maximum limit of ${MAX_COLUMNS} columns.`,
        };
      }

      totalCellCount += rowCount * colCount;
      if (totalCellCount > MAX_CELLS) {
        return {
          data: null,
          error: `Workbook exceeds total cell limit of ${MAX_CELLS.toLocaleString()} cells. Please split into smaller batches.`,
        };
      }
    }

    const sheetNames = workbook.worksheets.map((s) => s.name);

    // 3. Check for Canonical Template match (Purchase + Laptops or Accessories)
    const hasPurchaseSheet = sheetNames.some((n) => n.trim().toLowerCase() === 'purchase');
    const hasLaptopsSheet = sheetNames.some((n) => n.trim().toLowerCase() === 'laptops');
    const hasAccessoriesSheet = sheetNames.some((n) => n.trim().toLowerCase() === 'accessories');

    if (hasPurchaseSheet && (hasLaptopsSheet || hasAccessoriesSheet)) {
      const canonicalResult = parseCanonicalWorkbook(workbook);

      // BLANK TEMPLATE BYPASS: If canonical template was uploaded with 0 items, return cleanly without invoking Gemini!
      if (canonicalResult.items.length === 0) {
        return {
          data: {
            ...canonicalResult,
            warnings: ['Official import template detected. No inventory items were entered in the Laptops or Accessories sheets.'],
          },
          error: null,
        };
      }

      await enrichAndCheckDuplicates(canonicalResult);
      return { data: canonicalResult, error: null };
    }

    // 4. Try Heuristic Header Mapping (Deterministic multi-sheet parser)
    const heuristicResult = parseHeuristicWorkbook(workbook);
    if (heuristicResult && heuristicResult.items.length > 0) {
      await enrichAndCheckDuplicates(heuristicResult);
      return { data: heuristicResult, error: null };
    }

    // 5. Fallback to Gemini AI Mapping Plan (Interprets non-standard supplier layouts)
    const aiResult = await parseWithAiMapping(workbook);
    if (aiResult.notConfigured) {
      return { data: null, error: null, notConfigured: true };
    }
    if (aiResult.error || !aiResult.data) {
      return { data: null, error: aiResult.error || 'AI interpretation of spreadsheet failed.' };
    }

    await enrichAndCheckDuplicates(aiResult.data);
    return { data: aiResult.data, error: null };
  } catch (err: any) {
    return { data: null, error: err.message || 'An unexpected error occurred while parsing the spreadsheet.' };
  }
}

// ── Canonical Template Parser ──────────────────────────────────────────────────

function parseCanonicalWorkbook(workbook: ExcelJS.Workbook): ExtractedSpreadsheetPurchase {
  const wsPurchase = workbook.worksheets.find((s) => s.name.trim().toLowerCase() === 'purchase');
  const wsLaptops = workbook.worksheets.find((s) => s.name.trim().toLowerCase() === 'laptops');
  const wsAccessories = workbook.worksheets.find((s) => s.name.trim().toLowerCase() === 'accessories');

  // 1. Read Purchase Sheet (Field -> Value)
  const purchaseMeta: Record<string, string> = {};
  if (wsPurchase) {
    wsPurchase.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return; // Header
      const field = cleanString(row.getCell(1).value);
      const val = cleanString(row.getCell(2).value);
      if (field) purchaseMeta[field.toLowerCase()] = val;
    });
  }

  const items: ExtractedSpreadsheetItem[] = [];

  // 2. Read Laptops Sheet
  if (wsLaptops && wsLaptops.rowCount > 1) {
    const headerRow = wsLaptops.getRow(1);
    const colMap: Record<number, string> = {};
    headerRow.eachCell((cell, colNumber) => {
      const h = cleanString(cell.value);
      if (h) colMap[colNumber] = h;
    });

    for (let r = 2; r <= wsLaptops.rowCount; r++) {
      const row = wsLaptops.getRow(r);
      const rowObj: Record<string, unknown> = {};
      let hasAnyVal = false;

      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const headerName = colMap[colNumber];
        if (headerName) {
          const val = extractCellValue(cell);
          if (val !== '' && val !== null && val !== undefined) {
            hasAnyVal = true;
            rowObj[headerName] = val;
          }
        }
      });

      if (!hasAnyVal) continue;

      const brand = cleanString(rowObj['Brand']);
      const model = cleanString(rowObj['Model']);
      const desc = cleanString(rowObj['Description']);
      if (!brand && !model && !desc) continue;

      const unit_cost = Math.max(0, parseNumberOrNull(rowObj['Unit Cost (PKR)']) || 0);

      // NEVER default selling_price to unit_cost or manufactured multipliers
      const rawPrice = parseNumberOrNull(rowObj['Asking Price (PKR)']);
      const selling_price = rawPrice !== null && rawPrice > 0 ? rawPrice : undefined;

      const warnings: string[] = [];
      if (!brand) warnings.push('Brand is required');
      if (!model) warnings.push('Model is required');
      if (unit_cost <= 0) warnings.push('Unit cost is missing or 0');
      if (!selling_price) warnings.push('Asking price missing — requires owner review before listing');

      items.push({
        id: `laptop-${r}`,
        item_type: 'laptop',
        source_sheet: wsLaptops.name,
        source_row: r,
        original_description: desc || `${brand} ${model}`.trim(),
        brand: brand || undefined,
        model: model || undefined,
        serial_number: cleanString(rowObj['Serial Number']) || undefined,
        service_tag: cleanString(rowObj['Service Tag']) || undefined,
        barcode: cleanString(rowObj['Barcode']) || undefined,
        cpu: cleanString(rowObj['CPU']) || undefined,
        ram: cleanString(rowObj['RAM']) || undefined,
        storage: cleanString(rowObj['Storage']) || undefined,
        gpu: cleanString(rowObj['GPU']) || undefined,
        screen_size: cleanString(rowObj['Screen Size']) || undefined,
        resolution: cleanString(rowObj['Resolution']) || undefined,
        touchscreen: parseBoolean(rowObj['Touchscreen']),
        colour: cleanString(rowObj['Colour']) || undefined,
        keyboard_layout: cleanString(rowObj['Keyboard Layout']) || 'US',
        operating_system: cleanString(rowObj['Operating System']) || undefined,
        condition: parseCondition(rowObj['Condition']),
        grade: cleanString(rowObj['Grade']) || undefined,
        battery_health: parseNumberOrNull(rowObj['Battery Health %']),
        charger_included: parseBoolean(rowObj['Charger Included']) ?? true,
        charger_notes: cleanString(rowObj['Charger Notes']) || undefined,
        accessories_included: cleanString(rowObj['Accessories Included']) || undefined,
        unit_cost,
        refurbishment_cost: parseNumberOrNull(rowObj['Refurbishment Cost (PKR)']) || 0,
        other_cost: parseNumberOrNull(rowObj['Laptop Other Cost (PKR)']) || 0,
        selling_price,
        minimum_price: parseNumberOrNull(rowObj['Minimum Price (PKR)']) || undefined,
        warranty_months: parseNumberOrNull(rowObj['Warranty Months']) ?? 1,
        description: desc || undefined,
        notes: cleanString(rowObj['Notes']) || undefined,
        is_public: parseBoolean(rowObj['Public Listing']) ?? true,
        warnings,
        is_valid: warnings.length === 0,
      });
    }
  }

  // 3. Read Accessories Sheet
  if (wsAccessories && wsAccessories.rowCount > 1) {
    const headerRow = wsAccessories.getRow(1);
    const colMap: Record<number, string> = {};
    headerRow.eachCell((cell, colNumber) => {
      const h = cleanString(cell.value);
      if (h) colMap[colNumber] = h;
    });

    for (let r = 2; r <= wsAccessories.rowCount; r++) {
      const row = wsAccessories.getRow(r);
      const rowObj: Record<string, unknown> = {};
      let hasAnyVal = false;

      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const headerName = colMap[colNumber];
        if (headerName) {
          const val = extractCellValue(cell);
          if (val !== '' && val !== null && val !== undefined) {
            hasAnyVal = true;
            rowObj[headerName] = val;
          }
        }
      });

      if (!hasAnyVal) continue;

      const name = cleanString(rowObj['Name']);
      const sku = cleanString(rowObj['SKU']);
      const category = cleanString(rowObj['Category']) || 'Other Accessories';
      if (!name && !sku) continue;

      const quantity = Math.max(1, parseNumberOrNull(rowObj['Quantity']) || 1);
      const unit_cost = Math.max(0, parseNumberOrNull(rowObj['Unit Cost (PKR)']) || 0);

      const rawPrice = parseNumberOrNull(rowObj['Selling Price (PKR)']);
      const selling_price = rawPrice !== null && rawPrice > 0 ? rawPrice : undefined;

      const warnings: string[] = [];
      if (!name) warnings.push('Accessory name is required');
      if (unit_cost <= 0) warnings.push('Unit cost is missing or 0');
      if (!selling_price) warnings.push('Selling price missing — requires owner review');

      items.push({
        id: `acc-${r}`,
        item_type: 'accessory',
        source_sheet: wsAccessories.name,
        source_row: r,
        original_description: name || sku || 'Accessory',
        accessory_sku: sku || undefined,
        accessory_name: name || undefined,
        accessory_category: category,
        accessory_brand: cleanString(rowObj['Brand']) || undefined,
        accessory_barcode: cleanString(rowObj['Barcode']) || undefined,
        quantity,
        unit_cost,
        selling_price,
        reorder_level: parseNumberOrNull(rowObj['Reorder Level']) || 5,
        description: cleanString(rowObj['Description']) || undefined,
        is_public: parseBoolean(rowObj['Public Listing']) ?? true,
        warnings,
        is_valid: warnings.length === 0,
      });
    }
  }

  const pMethod = cleanString(purchaseMeta['payment method'])?.toLowerCase();
  const validPaymentMethod = ['cash', 'bank_transfer', 'mobile_wallet', 'other'].includes(pMethod || '')
    ? (pMethod as 'cash' | 'bank_transfer' | 'mobile_wallet' | 'other')
    : 'cash';

  return {
    supplier_name: purchaseMeta['supplier name'] || null,
    supplier_code: purchaseMeta['supplier code'] || null,
    invoice_reference: purchaseMeta['supplier invoice number'] || null,
    invoice_date: purchaseMeta['purchase date'] || new Date().toISOString().slice(0, 10),
    shipping_cost: parseNumberOrNull(purchaseMeta['shipping cost (pkr)']) || 0,
    other_cost: parseNumberOrNull(purchaseMeta['other purchase cost (pkr)']) || 0,
    discount: parseNumberOrNull(purchaseMeta['discount (pkr)']) || 0,
    amount_paid: parseNumberOrNull(purchaseMeta['amount paid (pkr)']) || 0,
    payment_method: validPaymentMethod,
    notes: purchaseMeta['notes'] || null,
    items,
    extraction_method: 'canonical_deterministic',
    warnings: [],
  };
}

// ── Heuristic Parser (Multi-sheet / supplier spreadsheets) ────────────────────

function parseHeuristicWorkbook(workbook: ExcelJS.Workbook): ExtractedSpreadsheetPurchase | null {
  const allItems: ExtractedSpreadsheetItem[] = [];
  let hasUnrecognizedDataSheet = false; // Track sheets skipped despite having data rows

  for (const ws of workbook.worksheets) {
    if (!ws || ws.rowCount < 2) continue;

    // Search for header row in top 10 rows of this sheet
    let headerRowNumber = -1;
    const colMap: Record<number, string> = {};

    for (let r = 1; r <= Math.min(10, ws.rowCount); r++) {
      const row = ws.getRow(r);
      const rowCells: string[] = [];
      row.eachCell((c) => {
        rowCells.push(cleanString(c.value).toLowerCase());
      });

      const hasBrandOrModel = rowCells.some((c) => c.includes('brand') || c.includes('make') || c.includes('model'));
      const hasCostOrPrice = rowCells.some((c) => c.includes('cost') || c.includes('price') || c.includes('rate') || c.includes('amount'));
      // Strong laptop signals required — a sheet with only "description" + "cost" is NOT a laptop sheet
      const hasSerialOrTag = rowCells.some((c) => c.includes('serial') || c.includes('service') || c.includes('tag'));
      const hasCpuOrRam = rowCells.some((c) => c.includes('cpu') || c.includes('processor') || c.includes('ram') || c.includes('memory'));
      const hasStorage = rowCells.some((c) => c.includes('ssd') || c.includes('hdd') || c.includes('storage') || c.includes('disk') || c.includes('nvme'));
      // Accessory signals
      const hasSkuOrQty = rowCells.some((c) => c === 'sku' || c === 'qty' || c === 'quantity' || c.includes('stock'));
      // Must have cost AND (serial/tag OR cpu+storage OR brand+model+cpu OR brand+model+storage)
      const isLaptopSheet = hasCostOrPrice && (
        hasSerialOrTag ||
        (hasCpuOrRam && hasStorage) ||
        (hasBrandOrModel && hasCpuOrRam) ||
        (hasBrandOrModel && hasStorage)
      );
      // Pure accessory structure: sku/qty/cost present but no laptop signals
      const isAccessorySheet = hasCostOrPrice && hasSkuOrQty && !isLaptopSheet;

      if (isAccessorySheet) {
        // Any sheet detected as accessory routes entire workbook to Gemini
        return null;
      }

      if (isLaptopSheet) {
        headerRowNumber = r;
        row.eachCell((c, colNumber) => {
          const field = classifyHeuristicColumn(cleanString(c.value));
          if (field) colMap[colNumber] = field;
        });
        break;
      }
    }

    // Sheet not recognised as a laptop sheet — skip it, but note if it had data rows
    if (headerRowNumber === -1) {
      // Check if the sheet has any non-empty data rows (beyond row 1)
      let hasDataRows = false;
      for (let r = 2; r <= ws.rowCount; r++) {
        const row = ws.getRow(r);
        let cellCount = 0;
        row.eachCell(() => { cellCount++; });
        if (cellCount > 0) { hasDataRows = true; break; }
      }
      if (hasDataRows) hasUnrecognizedDataSheet = true;
      continue;
    }

    for (let r = headerRowNumber + 1; r <= ws.rowCount; r++) {
      const row = ws.getRow(r);
      const rowObj: Record<string, unknown> = {};
      let hasVal = false;

      row.eachCell((c, colNum) => {
        const field = colMap[colNum];
        if (field) {
          const val = extractCellValue(c);
          if (val !== '' && val !== null && val !== undefined) {
            hasVal = true;
            rowObj[field] = val;
          }
        }
      });

      if (!hasVal) continue;

      const brand = cleanString(rowObj['brand']);
      const model = cleanString(rowObj['model']);
      const desc = cleanString(rowObj['description']);
      if (!brand && !model && !desc) continue;

      const firstCell = cleanString(row.getCell(1).value).toLowerCase();
      if (firstCell.includes('total') || firstCell.includes('subtotal') || firstCell.includes('summary')) continue;

      const unit_cost = Math.max(0, parseNumberOrNull(rowObj['unit_cost']) || 0);
      const rawPrice = parseNumberOrNull(rowObj['selling_price']);
      const selling_price = rawPrice !== null && rawPrice > 0 ? rawPrice : undefined;

      const rawQty = parseNumberOrNull(rowObj['quantity']) ?? 1;
      if (!Number.isSafeInteger(rawQty) || rawQty < 1 || rawQty > MAX_ROWS || allItems.length + rawQty > MAX_ROWS) {
        throw new Error(`Sheet "${ws.name}", row ${r}: quantity exceeds the 2000-unit limit or is not a positive whole number.`);
      }

      const baseItem: ExtractedSpreadsheetItem = {
        id: `heur-${ws.name}-${r}`,
        item_type: 'laptop',
        source_sheet: ws.name,
        source_row: r,
        original_description: desc || `${brand} ${model}`.trim(),
        brand: brand || 'Laptop',
        model: model || desc || 'Unknown Model',
        serial_number: cleanString(rowObj['serial_number']) || undefined,
        service_tag: cleanString(rowObj['service_tag']) || undefined,
        barcode: cleanString(rowObj['barcode']) || undefined,
        cpu: cleanString(rowObj['cpu']) || undefined,
        ram: cleanString(rowObj['ram']) || undefined,
        storage: cleanString(rowObj['storage']) || undefined,
        gpu: cleanString(rowObj['gpu']) || undefined,
        screen_size: cleanString(rowObj['screen_size']) || undefined,
        resolution: cleanString(rowObj['resolution']) || undefined,
        touchscreen: parseBoolean(rowObj['touchscreen']),
        colour: cleanString(rowObj['colour']) || undefined,
        keyboard_layout: cleanString(rowObj['keyboard_layout']) || 'US',
        operating_system: cleanString(rowObj['operating_system']) || undefined,
        condition: parseCondition(rowObj['condition']),
        grade: cleanString(rowObj['grade']) || undefined,
        battery_health: parseNumberOrNull(rowObj['battery_health']),
        charger_included: parseBoolean(rowObj['charger_included']) ?? true,
        charger_notes: cleanString(rowObj['charger_notes']) || undefined,
        accessories_included: cleanString(rowObj['accessories_included']) || undefined,
        unit_cost,
        quantity: 1,
        refurbishment_cost: parseNumberOrNull(rowObj['refurbishment_cost']) ?? 0,
        other_cost: parseNumberOrNull(rowObj['other_cost']) ?? 0,
        minimum_price: parseNumberOrNull(rowObj['minimum_price']) ?? undefined,
        description: desc || undefined,
        notes: cleanString(rowObj['notes']) || undefined,
        is_public: parseBoolean(rowObj['is_public']) ?? undefined,
        selling_price,
        warranty_months: parseNumberOrNull(rowObj['warranty_months']) ?? 1,
        warnings: [],
        is_valid: true,
      };

      if (!brand && !desc) baseItem.warnings.push('Missing brand');
      if (!model && !desc) baseItem.warnings.push('Missing model');
      if (!selling_price) baseItem.warnings.push('Asking price missing — requires owner review');
      baseItem.is_valid = baseItem.warnings.length === 0;

      // Expand quantity N into N individual physical-unit slots
      if (rawQty > 1) {
        const expanded = expandLaptopUnits(baseItem, rawQty);
        allItems.push(...expanded);
      } else {
        allItems.push(baseItem);
      }
    }
  }

  if (allItems.length === 0) return null;

  // If any sheet with data was skipped (unrecognized structure), the heuristic result
  // is partial — fall through to Gemini so it sees the full workbook.
  if (hasUnrecognizedDataSheet) return null;

  return {
    supplier_name: null,
    supplier_code: null,
    invoice_reference: null,
    invoice_date: new Date().toISOString().slice(0, 10),
    shipping_cost: 0,
    other_cost: 0,
    discount: 0,
    amount_paid: 0,
    payment_method: 'cash',
    notes: null,
    items: allItems,
    extraction_method: 'heuristic_deterministic',
    warnings: ['Parsed via header heuristics. Please verify mappings.'],
  };
}

// ── Column Mapping Resolver (Resolves Header Names to Column Indices) ──────────

export function resolveColumnMapping(
  headerRow: ExcelJS.Row,
  columnMapping: Record<string, string>
): Map<number, string> {
  const resolved = new Map<number, string>();
  if (!columnMapping || typeof columnMapping !== 'object') return resolved;

  // Build normalized lookup maps from columnMapping
  const normalizedKeyToField = new Map<string, string>();
  for (const [key, field] of Object.entries(columnMapping)) {
    const normKey = cleanString(key).toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normKey) {
      normalizedKeyToField.set(normKey, String(field).toLowerCase().trim());
    }
  }

  // Pass 1: Match actual header row cells by text
  headerRow.eachCell((cell, colNum) => {
    const colLetter = excelColLetter(colNum);
    const cellText = cleanString(cell.value);
    const cellNorm = cellText.toLowerCase().replace(/[^a-z0-9]/g, '');

    const field =
      columnMapping[colLetter] ||
      columnMapping[String(colNum)] ||
      columnMapping[cellText] ||
      normalizedKeyToField.get(cellNorm);

    if (field) {
      resolved.set(colNum, String(field).toLowerCase().trim());
    }
  });

  // Pass 2: Direct column letter/index fallbacks
  for (const [colKey, field] of Object.entries(columnMapping)) {
    if (/^[A-Za-z]+$/.test(colKey)) {
      const colNum = excelLetterToCol(colKey.toUpperCase());
      if (colNum > 0 && !resolved.has(colNum)) {
        resolved.set(colNum, String(field).toLowerCase().trim());
      }
    } else if (/^\d+$/.test(colKey)) {
      const colNum = parseInt(colKey, 10);
      if (colNum > 0 && !resolved.has(colNum)) {
        resolved.set(colNum, String(field).toLowerCase().trim());
      }
    }
  }

  return resolved;
}

// ── AI Mapping Plan Parser ─────────────────────────────────────────────────────

async function parseWithAiMapping(workbook: ExcelJS.Workbook): Promise<{
  data: ExtractedSpreadsheetPurchase | null;
  error: string | null;
  notConfigured?: boolean;
}> {
  // Build compact payload of up to 10 sheets, top 20 rows each
  const sheetsPayload = workbook.worksheets.slice(0, 10).map((ws) => {
    const sample_rows: Array<Record<string, unknown>> = [];
    for (let r = 1; r <= Math.min(20, ws.rowCount); r++) {
      const row = ws.getRow(r);
      const rowObj: Record<string, unknown> = { _row: r };
      row.eachCell((c, colNum) => {
        const val = extractCellValue(c);
        if (val !== '' && val !== null && val !== undefined) {
          rowObj[excelColLetter(colNum)] = val;
        }
      });
      sample_rows.push(rowObj);
    }
    return {
      sheet_name: ws.name,
      sample_rows,
    };
  });

  const { data: responseData, error: invokeError } = await supabase.functions.invoke(
    'extract-purchase-spreadsheet',
    {
      method: 'POST',
      body: {
        sheet_names: workbook.worksheets.map((s) => s.name),
        sheets: sheetsPayload,
      },
    }
  );

  if (invokeError) {
    const body = invokeError.context?.body ?? '';
    try {
      const parsed = JSON.parse(typeof body === 'string' ? body : JSON.stringify(body));
      if (parsed?.error === 'not_configured') {
        return { data: null, error: null, notConfigured: true };
      }
      return { data: null, error: parsed?.error ?? 'AI spreadsheet extraction failed' };
    } catch {
      return { data: null, error: 'Could not connect to AI spreadsheet service.' };
    }
  }

  if (!responseData?.success || !responseData?.plan) {
    return { data: null, error: responseData?.error || 'AI could not interpret spreadsheet structure.' };
  }

  const plan = responseData.plan;

  // Runtime plan validation
  const planSheets: Array<{
    sheet_name: string;
    item_type: 'laptop' | 'accessory';
    header_row_index: number;
    data_start_row_index: number;
    column_mapping: Record<string, string>;
  }> = Array.isArray(plan.sheets) && plan.sheets.length > 0
    ? plan.sheets
    : [
        {
          sheet_name: plan.primary_sheet || workbook.worksheets[0].name,
          item_type: plan.default_item_type || 'laptop',
          header_row_index: plan.header_row_index ?? 0,
          data_start_row_index: plan.data_start_row_index ?? 1,
          column_mapping: plan.column_mapping || {},
        },
      ];

  const items: ExtractedSpreadsheetItem[] = [];
  const warnings: string[] = Array.isArray(plan.warnings) ? [...plan.warnings] : [];
  const ignoredKeywords = (plan.ignored_row_keywords || ['total', 'subtotal', 'summary', 'vat', 'gst']).map((k: string) => k.toLowerCase());

  for (const sheetPlan of planSheets) {
    // Find matching worksheet by name — do NOT silently fallback to sheet 0
    const ws = workbook.worksheets.find((s) => s.name.trim().toLowerCase() === sheetPlan.sheet_name.trim().toLowerCase());
    if (!ws) {
      warnings.push(`Worksheet "${sheetPlan.sheet_name}" specified in AI plan was not found in workbook.`);
      continue;
    }

    const headerRowNumber = Math.max(1, (sheetPlan.header_row_index ?? 0) + 1);
    const headerRow = ws.getRow(headerRowNumber);
    const resolvedColMap = resolveColumnMapping(headerRow, sheetPlan.column_mapping || {});
    const startRow = Math.max(headerRowNumber + 1, (sheetPlan.data_start_row_index ?? 1) + 1); // 1-indexed in ExcelJS

    for (let r = startRow; r <= ws.rowCount; r++) {
      const row = ws.getRow(r);
      const rowObj: Record<string, unknown> = {};
      let hasVal = false;

      row.eachCell((c, colNum) => {
        const field = resolvedColMap.get(colNum);
        if (field) {
          const val = extractCellValue(c);
          if (val !== '' && val !== null && val !== undefined) {
            hasVal = true;
            rowObj[field] = val;
          }
        }
      });

      if (!hasVal) continue;

      const rowText = Object.values(rowObj).join(' ').toLowerCase();
      if (ignoredKeywords.some((k: string) => rowText.includes(k))) continue;

      const isAccessory = sheetPlan.item_type === 'accessory';

      if (isAccessory) {
        const name = cleanString(rowObj['name']) || cleanString(rowObj['description']);
        const sku = cleanString(rowObj['sku']);
        if (!name && !sku) continue;

        const quantity = Math.max(1, parseNumberOrNull(rowObj['quantity']) || 1);
        const unit_cost = Math.max(0, parseNumberOrNull(rowObj['unit_cost']) || 0);
        const rawPrice = parseNumberOrNull(rowObj['selling_price']);
        const selling_price = rawPrice !== null && rawPrice > 0 ? rawPrice : undefined;

        const itemWarnings: string[] = [];
        if (!name) itemWarnings.push('Name missing');
        if (unit_cost <= 0) itemWarnings.push('Unit cost is 0');
        if (!selling_price) itemWarnings.push('Selling price missing — requires owner review');

        items.push({
          id: `ai-acc-${ws.name}-${r}`,
          item_type: 'accessory',
          source_sheet: ws.name,
          source_row: r,
          original_description: name || sku || 'Accessory',
          accessory_sku: sku || undefined,
          accessory_name: name || undefined,
          accessory_category: cleanString(rowObj['category']) || 'Other Accessories',
          accessory_brand: cleanString(rowObj['brand']) || undefined,
          accessory_barcode: cleanString(rowObj['barcode']) || undefined,
          quantity,
          unit_cost,
          selling_price,
          reorder_level: parseNumberOrNull(rowObj['reorder_level']) || 5,
          description: cleanString(rowObj['description']) || undefined,
          is_public: parseBoolean(rowObj['is_public']) ?? true,
          warnings: itemWarnings,
          is_valid: itemWarnings.length === 0,
        });
      } else {
        const brand = cleanString(rowObj['brand']);
        const model = cleanString(rowObj['model']);
        const desc = cleanString(rowObj['description']);
        if (!brand && !model && !desc) continue;

        const unit_cost = Math.max(0, parseNumberOrNull(rowObj['unit_cost']) || 0);
        const rawPrice = parseNumberOrNull(rowObj['selling_price']);
        const selling_price = rawPrice !== null && rawPrice > 0 ? rawPrice : undefined;
        const rawQty = parseNumberOrNull(rowObj['quantity']) ?? 1;
        if (!Number.isSafeInteger(rawQty) || rawQty < 1 || rawQty > MAX_ROWS || items.length + rawQty > MAX_ROWS) {
          return { data: null, error: `Sheet "${ws.name}", row ${r}: quantity must be a positive whole number within the 2000-unit import limit.` };
        }

        const itemWarnings: string[] = [];
        if (!brand) itemWarnings.push('Brand missing');
        if (!model) itemWarnings.push('Model missing');
        if (unit_cost <= 0) itemWarnings.push('Unit cost is 0');
        if (!selling_price) itemWarnings.push('Asking price missing — requires owner review');

        const baseItem: ExtractedSpreadsheetItem = {
          id: `ai-laptop-${ws.name}-${r}`,
          item_type: 'laptop',
          source_sheet: ws.name,
          source_row: r,
          original_description: desc || `${brand} ${model}`.trim(),
          brand: brand || 'Laptop',
          model: model || desc || 'Unknown Model',
          serial_number: cleanString(rowObj['serial_number']) || undefined,
          service_tag: cleanString(rowObj['service_tag']) || undefined,
          barcode: cleanString(rowObj['barcode']) || undefined,
          cpu: cleanString(rowObj['cpu']) || undefined,
          ram: cleanString(rowObj['ram']) || undefined,
          storage: cleanString(rowObj['storage']) || undefined,
          gpu: cleanString(rowObj['gpu']) || undefined,
          screen_size: cleanString(rowObj['screen_size']) || undefined,
          resolution: cleanString(rowObj['resolution']) || undefined,
          touchscreen: parseBoolean(rowObj['touchscreen']),
          colour: cleanString(rowObj['colour']) || undefined,
          keyboard_layout: cleanString(rowObj['keyboard_layout']) || 'US',
          operating_system: cleanString(rowObj['operating_system']) || undefined,
          condition: parseCondition(rowObj['condition']),
          grade: cleanString(rowObj['grade']) || undefined,
          battery_health: parseNumberOrNull(rowObj['battery_health']),
          charger_included: parseBoolean(rowObj['charger_included']) ?? true,
          charger_notes: cleanString(rowObj['charger_notes']) || undefined,
          accessories_included: cleanString(rowObj['accessories_included']) || undefined,
          unit_cost,
          refurbishment_cost: parseNumberOrNull(rowObj['refurbishment_cost']) || 0,
          other_cost: parseNumberOrNull(rowObj['other_cost']) || 0,
          selling_price,
          minimum_price: parseNumberOrNull(rowObj['minimum_price']) || undefined,
          warranty_months: parseNumberOrNull(rowObj['warranty_months']) ?? 1,
          description: desc || undefined,
          notes: cleanString(rowObj['notes']) || undefined,
          is_public: parseBoolean(rowObj['is_public']) ?? true,
          warnings: itemWarnings,
          is_valid: itemWarnings.length === 0,
        };

        // Expand quantity N into N individual physical-unit slots
        if (rawQty > 1) {
          const expanded = expandLaptopUnits(baseItem, rawQty);
          items.push(...expanded);
        } else {
          items.push(baseItem);
        }
      }
    }
  }

  return {
    data: {
      supplier_name: plan.supplier_name || null,
      supplier_code: plan.supplier_code || null,
      invoice_reference: plan.invoice_reference || null,
      invoice_date: plan.invoice_date || new Date().toISOString().slice(0, 10),
      shipping_cost: Math.max(0, Number(plan.shipping_cost) || 0),
      other_cost: Math.max(0, Number(plan.other_cost) || 0),
      discount: Math.max(0, Number(plan.discount) || 0),
      amount_paid: Math.max(0, Number(plan.amount_paid) || 0),
      payment_method: plan.payment_method || 'cash',
      notes: plan.notes || null,
      items,
      extraction_method: 'ai_gemini',
      warnings,
    },
    error: null,
  };
}

// ── Duplicate Detection & Safe SKU Lookup ─────────────────────────────────────

async function enrichAndCheckDuplicates(purchase: ExtractedSpreadsheetPurchase): Promise<void> {
  const serialsInSheet = new Map<string, number[]>();
  const serviceTagsInSheet = new Map<string, number[]>();
  const barcodesInSheet = new Map<string, number[]>();

  purchase.items.forEach((item) => {
    if (item.serial_number) {
      const s = item.serial_number.toLowerCase().trim();
      serialsInSheet.set(s, [...(serialsInSheet.get(s) || []), item.source_row]);
    }
    if (item.service_tag) {
      const t = item.service_tag.toLowerCase().trim();
      serviceTagsInSheet.set(t, [...(serviceTagsInSheet.get(t) || []), item.source_row]);
    }
    if (item.barcode) {
      const b = item.barcode.toLowerCase().trim();
      barcodesInSheet.set(b, [...(barcodesInSheet.get(b) || []), item.source_row]);
    }
  });

  purchase.items.forEach((item) => {
    if (item.serial_number && (serialsInSheet.get(item.serial_number.toLowerCase().trim())?.length || 0) > 1) {
      item.duplicate_warning = `Duplicate serial number "${item.serial_number}" appears multiple times in spreadsheet.`;
      item.warnings.push(item.duplicate_warning);
      item.is_valid = false;
    }
    if (item.service_tag && (serviceTagsInSheet.get(item.service_tag.toLowerCase().trim())?.length || 0) > 1) {
      const w = `Duplicate service tag "${item.service_tag}" appears multiple times in spreadsheet.`;
      item.duplicate_warning = item.duplicate_warning ? `${item.duplicate_warning} | ${w}` : w;
      item.warnings.push(w);
      item.is_valid = false;
    }
  });

  // Query Database for existing serials / service tags
  const serialList = purchase.items.map((i) => i.serial_number?.trim()).filter(Boolean) as string[];
  const tagList = purchase.items.map((i) => i.service_tag?.trim()).filter(Boolean) as string[];

  if (serialList.length > 0) {
    const { data: dbLaptops } = await supabase
      .from('laptops')
      .select('id, stock_id, serial_number, service_tag, status')
      .in('serial_number', serialList);

    if (dbLaptops && dbLaptops.length > 0) {
      const dbSerialMap = new Map(dbLaptops.map((l) => [l.serial_number?.toLowerCase().trim(), l]));
      purchase.items.forEach((item) => {
        if (item.serial_number) {
          const match = dbSerialMap.get(item.serial_number.toLowerCase().trim());
          if (match) {
            const w = `Serial "${item.serial_number}" already exists in inventory as ${match.stock_id} (${match.status}).`;
            item.duplicate_warning = item.duplicate_warning ? `${item.duplicate_warning} | ${w}` : w;
            item.warnings.push(w);
            item.is_valid = false;
          }
        }
      });
    }
  }

  if (tagList.length > 0) {
    const { data: dbLaptopsTag } = await supabase
      .from('laptops')
      .select('id, stock_id, service_tag, status')
      .in('service_tag', tagList);

    if (dbLaptopsTag && dbLaptopsTag.length > 0) {
      const dbTagMap = new Map(dbLaptopsTag.map((l) => [l.service_tag?.toLowerCase().trim(), l]));
      purchase.items.forEach((item) => {
        if (item.service_tag) {
          const match = dbTagMap.get(item.service_tag.toLowerCase().trim());
          if (match) {
            const w = `Service Tag "${item.service_tag}" already exists in inventory as ${match.stock_id} (${match.status}).`;
            item.duplicate_warning = item.duplicate_warning ? `${item.duplicate_warning} | ${w}` : w;
            item.warnings.push(w);
            item.is_valid = false;
          }
        }
      });
    }
  }

  // Safe Accessory Matching: Exact normalized SKU lookup (case-insensitive & ambiguity safe)
  const hasAccessoryItems = purchase.items.some((i) => i.item_type === 'accessory' && i.accessory_sku);
  if (hasAccessoryItems) {
    try {
      const allActiveAccessories = await fetchAllActiveAccessories();
      const skuMap = new Map<string, typeof allActiveAccessories>();

      allActiveAccessories.forEach((acc) => {
        if (acc.sku) {
          const normSku = acc.sku.trim().toUpperCase();
          const list = skuMap.get(normSku) || [];
          list.push(acc);
          skuMap.set(normSku, list);
        }
      });

      purchase.items.forEach((item) => {
        if (item.item_type === 'accessory' && item.accessory_sku) {
          const normInputSku = item.accessory_sku.trim().toUpperCase();
          const matches = skuMap.get(normInputSku);

          if (matches && matches.length === 1) {
            const match = matches[0];
            item.accessory_id = match.id;
            item.exact_sku_matched = true;
            if (item.selling_price === undefined && match.selling_price) {
              item.selling_price = match.selling_price;
            }
            if (!item.accessory_name && match.name) {
              item.accessory_name = match.name;
            }
            if (!item.accessory_category && match.category) {
              item.accessory_category = match.category;
            }
          } else if (matches && matches.length > 1) {
            item.warnings.push(`Ambiguous SKU match: multiple active accessories found with SKU "${item.accessory_sku}".`);
          }
        }
      });
    } catch (accErr) {
      purchase.warnings.push('Could not verify accessory SKU matches against active database records.');
    }
  }

  // Check duplicate invoice reference against the authoritative supplier_invoice_number column.
  if (purchase.invoice_reference) {
    try {
      let query = supabase
        .from('purchases')
        .select('id, purchase_number, supplier_invoice_number, status')
        .eq('supplier_invoice_number', purchase.invoice_reference.trim())
        .neq('status', 'cancelled');

      if (purchase.supplier_id) {
        query = query.eq('supplier_id', purchase.supplier_id);
      }

      const { data: dbPurchases, error: invError } = await query.limit(1);

      if (invError) {
        purchase.warnings.push(`Could not verify duplicate invoice status: ${invError.message}`);
      } else if (dbPurchases && dbPurchases.length > 0) {
        purchase.warnings.push(
          `Warning: An active purchase already exists with invoice number "${purchase.invoice_reference}" (${dbPurchases[0].purchase_number}). Please verify this is not a duplicate purchase.`
        );
      }
    } catch {
      purchase.warnings.push('Could not perform duplicate invoice check due to a lookup error.');
    }
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function cleanString(val: unknown): string {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') {
    // ExcelJS rich text or formula value
    const anyVal = val as any;
    if (anyVal.text) return String(anyVal.text).trim().slice(0, MAX_STRING_LENGTH);
    if (anyVal.result) return String(anyVal.result).trim().slice(0, MAX_STRING_LENGTH);
  }
  return String(val).trim().slice(0, MAX_STRING_LENGTH);
}

export function extractCellValue(cell: ExcelJS.Cell): unknown {
  const v = cell.value;
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    const anyVal = v as any;
    if (anyVal.text !== undefined) return anyVal.text;
    if (anyVal.result !== undefined) return anyVal.result;
  }
  return v;
}

export function parseNumberOrNull(val: unknown): number | null {
  if (val === '' || val === null || val === undefined) return null;
  const num = Number(val);
  return isNaN(num) ? null : num;
}

export function parseBoolean(val: unknown): boolean | null {
  if (val === true || val === 1 || String(val).toLowerCase() === 'true' || String(val).toLowerCase() === 'yes' || String(val) === '1') {
    return true;
  }
  if (val === false || val === 0 || String(val).toLowerCase() === 'false' || String(val).toLowerCase() === 'no' || String(val) === '0') {
    return false;
  }
  return null;
}

export function parseCondition(val: unknown): 'new' | 'excellent' | 'good' | 'fair' | 'poor' {
  const norm = cleanString(val).toLowerCase();
  if (['new', 'excellent', 'good', 'fair', 'poor'].includes(norm)) {
    return norm as any;
  }
  return 'good';
}

export function excelColLetter(colNumber: number): string {
  let letter = '';
  let temp = colNumber;
  while (temp > 0) {
    const mod = (temp - 1) % 26;
    letter = String.fromCharCode(65 + mod) + letter;
    temp = Math.floor((temp - mod) / 26);
  }
  return letter;
}

export function excelLetterToCol(letter: string): number {
  let col = 0;
  for (let i = 0; i < letter.length; i++) {
    col = col * 26 + (letter.charCodeAt(i) - 64);
  }
  return col;
}

export function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}
